import { motion } from "framer-motion";
import {
  FaShieldAlt,
  FaChevronDown,
  FaRocket,
  FaChartLine,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Images from "../../utils/Images";

export default function Hero() {
  const navigate = useNavigate();

  const handleBecomeCustomer = () => {
    navigate("/signup");
  };

  const handleContactUs = () => {
    navigate("/support");
  };

  return (
    <section className="overflow-x-hidden relative pt-14 min-h-screen">
      {/* Enhanced Background with animated gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-900" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-green-500/10 via-transparent to-transparent" />

      {/* Animated grid pattern overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" />

      {/* Content Container */}
      <div className="relative z-10 flex items-center min-h-[calc(100vh-3.5rem)] py-8 lg:py-16">
        <div className="container px-4 mx-auto sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-10 items-center lg:grid-cols-2 lg:gap-12">
            {/* Left Content - Text Section */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="pt-8 text-center lg:text-left lg:pt-0"
            >
             

              {/* Main Headline - Shortened and improved */}
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="mb-6 text-3xl font-extrabold leading-tight text-white lg:mb-8 sm:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl"
              >
                Reach Thousands of{" "}
                <span className="relative inline-block">
                  <span className="relative z-10 bg-gradient-to-r from-green-400 via-emerald-400 to-teal-400 bg-clip-text text-transparent">
                    New Clients Daily
                  </span>
                  <motion.span
                    className="absolute bottom-0 left-0 w-full h-3 bg-green-400/20 -z-0"
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 0.8, delay: 1.2 }}
                  />
                </span>
                <br className="hidden lg:block" />
                <span className="block mt-3 text-2xl font-semibold text-gray-300 lg:mt-4 sm:text-3xl lg:text-4xl xl:text-5xl">
                  Sell insurance Seamlessly.
                </span>
              </motion.h1>

              {/* Enhanced Subtitle with features */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="mx-auto mb-8 max-w-2xl lg:mb-10 lg:mx-0"
              >
                <p className="mb-4 text-base text-gray-300 lg:text-lg sm:text-xl">
                  Partner with TripSecure to digitize distribution, automate
                  policies, and drive real-time premiums from multiple channels.
                </p>

                {/* Feature Pills */}
                <div className="flex flex-wrap gap-2 justify-center lg:justify-start">
                  {["Web", "SMS", "USSD"].map((channel, index) => (
                    <motion.div
                      key={channel}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.4, delay: 1.0 + index * 0.1 }}
                      className="px-3 py-1.5 text-xs font-medium text-green-400 rounded-full border backdrop-blur-sm lg:text-sm bg-green-400/10 border-green-400/30"
                    >
                      {channel}
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* CTA Buttons - Enhanced */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.0 }}
                className="flex flex-col gap-3 justify-center sm:flex-row lg:gap-4 lg:justify-start"
              >
                <motion.button
                  onClick={handleBecomeCustomer}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="group relative px-8 py-4 text-sm font-semibold text-black bg-white rounded-full shadow-xl transition-all duration-300 lg:px-10 lg:py-4 hover:shadow-2xl hover:shadow-green-400/20 lg:text-base overflow-hidden"
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    <FaRocket className="w-4 h-4" />
                    Become a Customer
                  </span>
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    initial={false}
                  />
                </motion.button>
                <motion.button
                  onClick={handleContactUs}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 text-sm font-semibold text-white rounded-full border-2 backdrop-blur-md transition-all duration-300 lg:px-10 lg:py-4 border-white/30 bg-white/5 hover:bg-white/10 hover:border-white/50 hover:shadow-lg hover:shadow-white/10 lg:text-base"
                >
                  Contact Us
                </motion.button>
              </motion.div>
            </motion.div>

            {/* Right illustration — unconstrained, sits on the page */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="relative flex order-first justify-center lg:order-last lg:justify-end"
            >
              <div className="absolute inset-8 rounded-full bg-emerald-400/15 blur-3xl pointer-events-none lg:inset-4" />
              <img
                src={Images.hero_img}
                alt="TripSecure insurance platform across web, travel, and mobile"
                className="relative z-10 w-full max-w-md drop-shadow-2xl select-none mix-blend-screen sm:max-w-lg lg:max-w-none lg:w-[115%] lg:-mr-8 xl:-mr-16"
              />
            </motion.div>
          </div>
        </div>
      </div>

      {/* Enhanced Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.4 }}
        className="absolute bottom-4 left-1/2 -translate-x-1/2 lg:bottom-8"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          className="flex justify-center items-center w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full shadow-lg backdrop-blur-sm border border-white/20 cursor-pointer hover:scale-110 transition-transform duration-300 lg:w-14 lg:h-14"
        >
          <FaChevronDown size={18} className="text-white lg:w-6 lg:h-6" />
        </motion.div>
      </motion.div>
    </section>
  );
}
